/* Offscreen Play, customer video rail and its full-screen viewer.
   Moved out of snippets/op-ugc-rail.liquid, Sep 2026 (page speed step 2):
   31 KB that used to ride inline with every product page, now cached and
   deferred. NOTHING IN HERE MAY BE LIQUID. How it loads and why is documented
   at the top of the snippet. */
(function () {
  function setup(root) {
    if (!root || root.dataset.opUgcInit) return;
    root.dataset.opUgcInit = '1';

    const rail = root.querySelector('[data-op-ugc-rail]');
    const cards = Array.from(root.querySelectorAll('[data-op-ugc-card]'));
    if (!rail || !cards.length) return;

    const conn = navigator.connection || {};
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // No autoplay means no video bytes until a tap: poster only.
    const lite = conn.saveData === true || /(^|-)2g$/.test(conn.effectiveType || '') || reducedMotion;
    const smooth = !reducedMotion;
    // Seconds of each clip the rail loops. The full clip plays only in the viewer.
    const PREVIEW = Math.max(2, parseFloat(root.dataset.preview) || 5);
    // 'maybe' is the strongest answer canPlayType ever gives for HLS.
    const canHls = (() => {
      try { return document.createElement('video').canPlayType('application/vnd.apple.mpegurl') !== ''; } catch (err) { return false; }
    })();
    /* No H.264 at all (some test and headless browsers): posters only, like
       Save-Data. Found Sep 2026 in PageSpeed's browser: every preview clip
       errored there and the old fallback then pulled all three full clips.
       Real users should never hit this. */
    const canMp4 = (() => {
      try { return document.createElement('video').canPlayType('video/mp4; codecs="avc1.42E01E"') !== ''; } catch (err) { return false; }
    })();

    /* ---- arrows (mouse convenience; keyboard scrolls the focused rail) ---- */
    const nav = root.querySelector('.op-ugc__nav');
    const prev = root.querySelector('[data-op-ugc-prev]');
    const next = root.querySelector('[data-op-ugc-next]');
    const syncArrows = () => {
      if (!nav) return;
      const max = rail.scrollWidth - rail.clientWidth;
      nav.classList.toggle('is-scrollable', max > 4);
      prev.disabled = rail.scrollLeft <= 4;
      next.disabled = rail.scrollLeft >= max - 4;
    };
    const step = (dir) => {
      const card = cards[0].getBoundingClientRect().width + parseFloat(getComputedStyle(rail).columnGap || 12);
      const n = Math.max(1, Math.floor(rail.clientWidth / card) - 1);
      rail.scrollBy({ left: dir * n * card, behavior: smooth ? 'smooth' : 'auto' });
    };
    if (nav) {
      prev.addEventListener('click', () => step(-1));
      next.addEventListener('click', () => step(1));
      rail.addEventListener('scroll', () => requestAnimationFrame(syncArrows), { passive: true });
      window.addEventListener('resize', syncArrows, { passive: true });
      syncArrows();
    }

    /* ---- per-card state ---- */
    const setState = (card) => {
      const video = card._video;
      card.dataset.state = !video || video.paused ? 'paused' : 'playing';
    };

    const ready = (card) => {
      if (card.classList.contains('is-ready')) return;
      card.classList.add('is-ready');
      card.querySelector('[data-op-ugc-btn]').hidden = false;
      setState(card);
    };

    /* Smallest mp4 for a card, worked out from its HLS master playlist.
       "x.SD-480p.1.5Mbps.hls.123_.m3u8" is the stream twin of
       "x.SD-480p-1.5Mbps-123.mp4". Resolves to nothing on any surprise. */
    const findSmall = (card) => {
      const master = card.dataset.srcHls;
      if (!master || !/\.m3u8(\?|$)/.test(master) || !window.fetch) return Promise.resolve();
      const ctl = window.AbortController ? new AbortController() : null;
      const timer = setTimeout(() => ctl && ctl.abort(), 2000);
      return fetch(master, ctl ? { signal: ctl.signal } : {})
        .then((r) => (r.ok ? r.text() : ''))
        .then((txt) => {
          let best = null;
          const lines = txt.split(/\r?\n/);
          lines.forEach((line, i) => {
            const res = /^#EXT-X-STREAM-INF:.*RESOLUTION=\d+x(\d+)/.exec(line);
            const m = res && /^(.+)\.((?:SD|HD)-\d+p)\.([\d.]+Mbps)\.hls\.(\d+)_\.m3u8$/.exec((lines[i + 1] || '').trim());
            if (m && (!best || +res[1] < best.h)) best = { h: +res[1], file: m[1] + '.' + m[2] + '-' + m[3] + '-' + m[4] + '.mp4' };
          });
          if (best) card.dataset.srcSmall = new URL(best.file, new URL(master, location.href)).href;
        })
        .catch(() => {})
        .then(() => clearTimeout(timer));
    };

    const buildVideo = (card, forViewer) => {
      if (card._video) return card._video;
      const video = document.createElement('video');
      video.muted = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('muted', '');
      video.preload = lite ? 'auto' : 'metadata';
      video.disablePictureInPicture = true;
      video.tabIndex = -1;
      video.setAttribute('aria-hidden', 'true');
      video.addEventListener('loadeddata', () => ready(card), { once: true });
      const hls = card.dataset.srcHls;
      const small = card.dataset.srcSmall || '';
      const useHls = !small && !!hls && hls !== card.dataset.src && canHls;
      const full = small || (useHls ? hls : card.dataset.src);
      /* PREVIEW CLIP (see PREVIEW CLIPS in the snippet). With one, the rail
         loops that short file and the full clip is loaded only when the card
         is opened. onPreview says which of the two the element holds now.
         A preview that fails to load is dropped for good (preview = ''). */
      let preview = card.dataset.srcPreview || '';
      let onPreview = !!preview && !forViewer;
      // 'preview' | 'full' | 'none' (a preview that failed: poster only until a tap).
      let loaded = onPreview ? 'preview' : 'full';

      /* LOOPING. The `loop` attribute is only trusted on the plain mp4.
         On a native HLS stream it is not: reaching the end tears the stream
         down and rebuilds it, and on some phones that handoff breaks. Seen on
         this store, Sep 2026, on an 11.9s clip whose stream is padded to
         12.0s: a flash of the first frame, back to the last frame, then the
         audio restarts while the picture stays frozen and play does nothing.
         So a stream never reaches its end at all. Just before it, the clip is
         sought back to the start, which is an ordinary seek and works
         everywhere. `ended` is kept only as a net for a missed tick. */
      video.loop = onPreview || !useHls;
      const restart = () => {
        try { video.currentTime = openCard === card ? 0 : pStart(); } catch (err) {}
        if (!video.paused || video.ended) safePlay(video);
      };

      /* WHERE THE CLIP REALLY ENDS. `duration` cannot be trusted on a stream.
         Measured on this store: the 11.9s clip reports 12.00 but its media
         stops at 11.57. Playback that reaches 11.57 does not end, it waits
         for data that does not exist, so a restart point worked out from
         `duration` alone (the first version used duration - 0.45 = 11.55)
         sits a fiftieth of a second from the cliff. Pausing in the last
         second and pressing play ran straight off it: the clip stalled at
         11.57 and never reached the restart point, so play "did nothing".
         When the buffered range stops within a second of `duration`, that
         stop IS the end of the media (a real gap in buffering is never that
         close to the end without closing). */
      const endOf = () => {
        const d = video.duration;
        if (!isFinite(d)) return Infinity;
        const b = video.buffered;
        if (b.length) {
          const e = b.end(b.length - 1);
          if (e < d && d - e < 1) return e;
        }
        return d;
      };
      video._opEnd = endOf;

      /* Where this card's preview window starts (the block's "Preview starts
         at"). A start typed in past the end of the clip, or so close to it
         that there is no window left, is pulled back so a full preview still
         fits: a wrong number gives a sensible loop, never a stuck card. */
      const wantStart = Math.max(0, parseFloat(card.dataset.start) || 0);
      // This clip's own preview length, else the section's. Under a second
      // is treated as a typo: a loop that short is a flicker, not a preview.
      const ownLength = parseFloat(card.dataset.length);
      const LENGTH = ownLength >= 1 ? ownLength : PREVIEW;
      function pStart() {
        // A preview clip is the window itself.
        if (onPreview) return 0;
        const end = endOf();
        if (!isFinite(end)) return wantStart;
        return Math.max(0, Math.min(wantStart, end - LENGTH));
      }
      card._pStart = pStart;
      // Jump to the window as soon as seeking is possible, which is before
      // the first frame is shown, so the card never flashes 0:00 first.
      if (wantStart > 0) {
        video.addEventListener('loadedmetadata', () => {
          if (openCard !== card) { try { video.currentTime = pStart(); } catch (err) {} }
        });
      }

      /* Where playback should turn round RIGHT NOW. In the rail that is the
         end of the preview; in the viewer it is the end of the clip. In the
         rail the turn is always done by hand, even on a plain mp4 whose
         `loop` attribute would otherwise handle it, because `loop` only
         knows about the real end. */
      const inViewer = () => openCard === card;
      const limit = () => (inViewer() || onPreview ? endOf() : Math.min(endOf(), pStart() + LENGTH));
      const byHand = () => !video.loop || !inViewer();

      /* timeupdate only ticks about four times a second, far too coarse to
         stop 0.12s short of the end. So it is used only to notice the final
         stretch, and from there the position is checked every frame. */
      let watching = false;
      const watchEnd = () => {
        if (!byHand() || video.paused) { watching = false; return; }
        const end = limit();
        if (video.currentTime >= end - 0.12) { watching = false; restart(); return; }
        if (video.currentTime < end - 1.6) { watching = false; return; }
        requestAnimationFrame(watchEnd);
      };
      video.addEventListener('timeupdate', () => {
        if (!inViewer() && !video.seeking && video.currentTime < pStart() - 0.4) {
          try { video.currentTime = pStart(); } catch (err) {}
          return;
        }
        if (!byHand() || watching || video.seeking || video.paused) return;
        if (video.currentTime >= limit() - 1.5) { watching = true; requestAnimationFrame(watchEnd); }
      });
      // Play pressed with almost nothing left: start over, like any loop.
      video.addEventListener('play', () => {
        if (byHand() && video.currentTime >= limit() - 0.3) restart();
      });
      // Nets, for a hidden tab (no animation frames) or a missed frame.
      video.addEventListener('ended', () => { if (!video.loop) restart(); });
      video.addEventListener('waiting', () => {
        if (!video.loop && isFinite(video.duration) && video.currentTime >= video.duration - 1.5) restart();
      });

      /* If a stream misbehaves anyway, this card drops to the plain mp4 for
         good, at the same timestamp and with the same sound setting. */
      let triedMp4 = !useHls && !small;
      card._toMp4 = () => {
        if (triedMp4) return false;
        triedMp4 = true;
        const at = video.currentTime || 0;
        const wasPaused = video.paused;
        video.loop = true;
        video.addEventListener('loadedmetadata', () => {
          try { video.currentTime = Math.min(at, Math.max(0, video.duration - 0.1)); } catch (err) {}
          if (!wasPaused) safePlay(video);
        }, { once: true });
        video.src = card.dataset.src;
        return true;
      };
      // Opened: the preview gives way to the full clip. Runs inside the tap,
      // just before openViewer's play(), so sound is still allowed. Closing
      // hands the card its preview back, except on Save-Data and the like,
      // where the rail does not autoplay and would only fetch it for nothing.
      card._toFull = () => {
        if (loaded === 'full') return;
        loaded = 'full';
        onPreview = false;
        video.loop = !useHls;
        video.src = full;
      };
      card._toPreview = () => {
        if (lite || !preview || onPreview) return;
        onPreview = true;
        loaded = 'preview';
        video.loop = true;
        video.src = preview;
      };
      video.addEventListener('error', () => {
        // A preview clip that will not load: the card keeps its poster and
        // the full clip loads only on a tap. It used to fall back to looping
        // the full clip, which in a browser that cannot play the preview
        // (PageSpeed's, Sep 2026) meant downloading all three whole.
        if (onPreview) {
          preview = '';
          onPreview = false;
          loaded = 'none';
          video.removeAttribute('src');
          try { video.load(); } catch (err) {}
          return;
        }
        // The stream failed but the plain file has not been tried: try it.
        if (card._toMp4()) return;
        // Poster already showing: leave it. Nothing showing: drop the card so
        // a dead file never leaves a permanent shimmer.
        if (!card.classList.contains('is-ready')) { card.remove(); syncArrows(); }
      });
      ['play', 'pause'].forEach((e) => video.addEventListener(e, () => { setState(card); syncViewer(); }));
      video.addEventListener('volumechange', () => syncViewer());
      ['timeupdate', 'durationchange', 'seeked'].forEach((e) => video.addEventListener(e, () => { if (openCard === card) syncTime(); }));
      video.src = onPreview ? preview : full;
      card._video = video;
      if (card._hookPlay) { card._hookPlay(); card._hookPlay = null; }
      card.insertBefore(video, card.querySelector('[data-op-ugc-btn]'));
      return video;
    };

    /* `under` is the poster as a floor beneath an autoplaying clip, not
       instead of one. A card used to become tappable only on the video's
       `loadeddata`, and iOS Safari does not fire that on preload="metadata"
       until playback starts. With autoplay refused (Low Power Mode, or
       Auto-Play switched off) that left a shimmering tile whose button was
       still hidden, so a tap did nothing at all. With the poster under it the
       card shows a picture and opens on a tap whatever the video is doing. A
       failed poster only removes the card when the poster is all there is. */
    const buildPoster = (card, under) => {
      if (card.querySelector('.op-ugc__poster')) return;
      const img = new Image();
      img.className = 'op-ugc__poster';
      img.alt = '';
      img.decoding = 'async';
      img.onload = () => ready(card);
      img.onerror = () => { if (!under) { card.remove(); syncArrows(); } };
      /* Sized to the card, at most 2x sharp (page speed, Sep 2026). A 3x
         phone is told the card is two-thirds its width, so it takes the 2x
         file: 280px for a 140px card, against 540px before. The still is a
         touch softer there, and usually has a clip playing over it. */
      if (card.dataset.posterSrcset) {
        const dpr = window.devicePixelRatio || 1;
        const w = card.getBoundingClientRect().width || 140;
        // Rounded down: 94px on a 3x phone would ask for 282 and fetch 360.
        img.sizes = Math.floor(w * Math.min(dpr, 2) / dpr) + 'px';
        img.srcset = card.dataset.posterSrcset;
      }
      img.src = card.dataset.poster;
      // Before the video, so the video paints over it once it has a frame.
      card.insertBefore(img, card._video && card._video.parentNode === card ? card._video : card.querySelector('[data-op-ugc-btn]'));
    };

    const safePlay = (video) => {
      const p = video.play();
      if (p && p.catch) p.catch(() => {});
    };

    /* ---- full-screen viewer ----
       Built on first tap, so a visitor who never opens a clip pays for none
       of it. Lives on <body> so no ancestor's transform, overflow or stacking
       context can clip a position:fixed child. */
    let lb = null;
    let openCard = null;
    let lastFocus = null;
    let historyPushed = false;

    const fmt = (t) => {
      t = Math.max(0, Math.floor(t || 0));
      return Math.floor(t / 60) + ':' + String(t % 60).padStart(2, '0');
    };
    function syncTime() {
      if (!lb || !openCard || !openCard._video) return;
      const video = openCard._video;
      const d = isFinite(video.duration) ? video.duration : 0;
      const pct = d ? (video.currentTime / d) * 100 : 0;
      lb.cur.textContent = fmt(video.currentTime);
      lb.dur.textContent = fmt(d);
      if (!lb.scrubbing) lb.seek.value = String(Math.round(pct * 10));
      lb.seek.style.setProperty('--op-ugc-pct', pct + '%');
      lb.seek.setAttribute('aria-valuetext', fmt(video.currentTime) + ' of ' + fmt(d));
    }

    function syncViewer() {
      syncTime();
      if (!lb || !openCard || !openCard._video) return;
      lb.root.classList.toggle('is-paused', openCard._video.paused);
      lb.mute.classList.toggle('is-muted', openCard._video.muted);
      lb.mute.setAttribute('aria-label', openCard._video.muted ? 'Turn sound on' : 'Turn sound off');
    }

    const buildViewer = () => {
      const el = document.createElement('div');
      el.className = 'op-ugc-lb';
      el.hidden = true;
      el.setAttribute('role', 'dialog');
      el.setAttribute('aria-modal', 'true');
      el.innerHTML =
        '<div class="op-ugc-lb__shade"></div>' +
        '<div class="op-ugc-lb__stage" data-stage><span class="op-ugc-lb__paused" aria-hidden="true"><svg viewBox="0 0 24 24" width="28" height="28" focusable="false"><path d="M8 5.5v13l11-6.5-11-6.5Z" fill="currentColor"/></svg></span></div>' +
        '<button type="button" class="op-ugc-lb__ctl op-ugc-lb__mute" data-mute>' +
          '<svg data-off viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false"><path d="M4 9.5v5h3.5L12 18.5v-13L7.5 9.5H4Z" fill="currentColor"/><path d="M15.5 9a4.2 4.2 0 0 1 0 6M18 6.5a7.8 7.8 0 0 1 0 11" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>' +
          '<svg data-on viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false"><path d="M4 9.5v5h3.5L12 18.5v-13L7.5 9.5H4Z" fill="currentColor"/><path d="M16 9.5l5 5m0-5l-5 5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>' +
        '</button>' +
        '<button type="button" class="op-ugc-lb__ctl op-ugc-lb__close" data-close aria-label="Close video">' +
          '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>' +
        '</button>' +
        '<div class="op-ugc-lb__bar" data-bar>' +
          '<button type="button" class="op-ugc-lb__skip" data-skip="-10" aria-label="Back 10 seconds">' +
            '<svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false"><path d="M12 5a7.5 7.5 0 1 1-7.2 5.4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/><path d="M12 1.8 8.6 5 12 8.2" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><text x="12" y="15.6" text-anchor="middle" font-size="7" font-weight="700" fill="currentColor" font-family="inherit">10</text></svg>' +
          '</button>' +
          '<span class="op-ugc-lb__time" data-cur>0:00</span>' +
          '<input class="op-ugc-lb__seek" data-seek type="range" min="0" max="1000" step="1" value="0" aria-label="Video position">' +
          '<span class="op-ugc-lb__time" data-dur>0:00</span>' +
          '<button type="button" class="op-ugc-lb__skip" data-skip="10" aria-label="Forward 10 seconds">' +
            '<svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false"><path d="M12 5a7.5 7.5 0 1 0 7.2 5.4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/><path d="M12 1.8 15.4 5 12 8.2" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><text x="12" y="15.6" text-anchor="middle" font-size="7" font-weight="700" fill="currentColor" font-family="inherit">10</text></svg>' +
          '</button>' +
        '</div>' +
        '<span class="op-ugc-lb__hint" aria-hidden="true"></span>';
      document.body.appendChild(el);

      const v = {
        root: el,
        shade: el.querySelector('.op-ugc-lb__shade'),
        stage: el.querySelector('[data-stage]'),
        mute: el.querySelector('[data-mute]'),
        close: el.querySelector('[data-close]'),
        seek: el.querySelector('[data-seek]'),
        cur: el.querySelector('[data-cur]'),
        dur: el.querySelector('[data-dur]'),
        scrubbing: false,
      };

      /* Timeline. While a finger is on the slider the video's own timeupdate
         must not write to it, or the thumb fights the finger. */
      const seekTo = (t) => {
        const video = openCard && openCard._video;
        if (!video || !isFinite(video.duration)) return;
        // Short of the end, and short of the restart window above it: landing
        // inside either snaps the clip to 0:00, which reads as the scrub
        // having failed.
        const end = video._opEnd ? Math.min(video._opEnd(), video.duration) : video.duration;
        video.currentTime = Math.min(Math.max(0, t), Math.max(0, end - 0.4));
        syncTime();
      };
      v.seek.addEventListener('pointerdown', () => { v.scrubbing = true; });
      ['pointerup', 'pointercancel', 'change'].forEach((n) => v.seek.addEventListener(n, () => { v.scrubbing = false; }));
      v.seek.addEventListener('input', () => {
        const video = openCard && openCard._video;
        if (!video || !isFinite(video.duration)) return;
        seekTo((v.seek.value / 1000) * video.duration);
      });
      el.querySelectorAll('[data-skip]').forEach((b) => b.addEventListener('click', () => {
        const video = openCard && openCard._video;
        if (video) seekTo(video.currentTime + parseFloat(b.dataset.skip));
      }));

      v.close.addEventListener('click', () => closeViewer());
      v.mute.addEventListener('click', () => {
        if (openCard && openCard._video) openCard._video.muted = !openCard._video.muted;
      });

      el.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') { e.preventDefault(); closeViewer(); return; }
        const video = openCard && openCard._video;
        // The slider handles its own arrow keys; everywhere else they skip.
        if ((e.key === 'ArrowLeft' || e.key === 'ArrowRight') && e.target !== v.seek && video) {
          e.preventDefault();
          seekTo(video.currentTime + (e.key === 'ArrowLeft' ? -10 : 10));
          return;
        }
        if (e.key !== 'Tab') return;
        const stops = Array.from(el.querySelectorAll('button, input'));
        const i = stops.indexOf(document.activeElement);
        e.preventDefault();
        stops[(i + (e.shiftKey ? -1 : 1) + stops.length) % stops.length].focus();
      });

      /* Drag to dismiss. Pointer events cover touch, pen and mouse in one
         path. Only a downward drag moves the clip; up and sideways are
         ignored so a sloppy tap still counts as a tap. */
      let drag = null;
      el.addEventListener('pointerdown', (e) => {
        if (e.target.closest('button, [data-bar]') || (e.pointerType === 'mouse' && e.button !== 0)) return;
        drag = { id: e.pointerId, x: e.clientX, y: e.clientY, t: performance.now(), dy: 0, moved: false };
        try { el.setPointerCapture(e.pointerId); } catch (err) {}
      });
      el.addEventListener('pointermove', (e) => {
        if (!drag || e.pointerId !== drag.id) return;
        const dy = e.clientY - drag.y;
        const dx = e.clientX - drag.x;
        if (!drag.moved && Math.abs(dy) < 8 && Math.abs(dx) < 8) return;
        drag.moved = true;
        drag.dy = Math.max(0, dy);
        el.classList.add('is-dragging');
        const h = window.innerHeight || 1;
        const k = Math.min(1, drag.dy / h);
        v.stage.style.transform = 'translateY(' + drag.dy + 'px) scale(' + (1 - k * 0.25) + ')';
        v.shade.style.opacity = String(1 - Math.min(1, k * 1.6));
      });
      const endDrag = (e, cancelled) => {
        if (!drag || e.pointerId !== drag.id) return;
        const d = drag;
        drag = null;
        el.classList.remove('is-dragging');
        const speed = d.dy / Math.max(1, performance.now() - d.t); // px per ms
        const dismiss = !cancelled && d.moved && (d.dy > 110 || (d.dy > 40 && speed > 0.55));
        if (dismiss) { closeViewer(true); return; }
        v.stage.style.transform = '';
        v.shade.style.opacity = '';
        if (!cancelled && !d.moved && openCard && openCard._video) {
          const video = openCard._video;
          if (video.paused || video.ended) {
            if (video.ended) { try { video.currentTime = 0; } catch (err) {} }
            safePlay(video);
          } else {
            video.pause();
          }
        }
      };
      el.addEventListener('pointerup', (e) => endDrag(e, false));
      el.addEventListener('pointercancel', (e) => endDrag(e, true));

      return v;
    };

    const openViewer = (card) => {
      if (openCard) return;
      if (!lb) lb = buildViewer();
      const video = buildVideo(card, true);
      // A card looping its preview clip swaps to the full clip here.
      if (card._toFull) card._toFull();
      openCard = card;
      lastFocus = document.activeElement;

      cards.forEach((c) => { if (c !== card && c._video) c._video.pause(); });

      lb.root.setAttribute('aria-label', card.querySelector('[data-op-ugc-btn]').getAttribute('aria-label').split(':')[0]);
      lb.stage.style.transform = '';
      lb.shade.style.opacity = '';
      lb.stage.insertBefore(video, lb.stage.firstChild);
      lb.root.hidden = false;
      document.documentElement.style.overflow = 'hidden';

      // The rail was only looping the preview. Opened, the clip plays in full
      // from the top.
      try { video.currentTime = 0; } catch (err) {}

      // Same tick as the tap, so the browser still counts this as a user
      // gesture and allows sound. Moving a <video> in the DOM pauses it in
      // some engines, hence the explicit play.
      video.muted = false;
      safePlay(video);
      syncViewer();

      // Two frames: one for [hidden] to come off, one for the transition to
      // have a starting state to animate from.
      requestAnimationFrame(() => requestAnimationFrame(() => lb.root.classList.add('is-open')));
      lb.close.focus({ preventScroll: true });

      // Back button closes the viewer instead of leaving the product page.
      try { history.pushState({ opUgc: true }, ''); historyPushed = true; } catch (err) { historyPushed = false; }
    };

    function closeViewer(flung, fromPop) {
      if (!openCard) return;
      const card = openCard;
      const video = card._video;
      openCard = null;
      // Silent at once: for the length of the closing animation the clip is
      // already back under preview rules and may jump to 0:00.
      if (video) video.muted = true;

      const finish = () => {
        lb.root.hidden = true;
        lb.root.classList.remove('is-open');
        lb.stage.style.transform = '';
        lb.shade.style.opacity = '';
        if (video) {
          video.muted = true;
          // Back to the preview clip where there is one.
          if (card._toPreview) card._toPreview();
          // Back to the top, so the card resumes inside its preview window
          // rather than wherever the full clip had got to.
          try { video.currentTime = card._pStart ? card._pStart() : 0; } catch (err) {}
          card.insertBefore(video, card.querySelector('[data-op-ugc-btn]'));
        }
        // Opening paused every clip in the rail, and the observer that plays
        // them only fires when visibility CHANGES, which closing does not
        // cause. So put each one back by hand: muted, and looping only if it
        // is on screen and autoplay is allowed at all.
        resumeVisible();
        document.documentElement.style.overflow = '';
        if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
      };

      if (flung) {
        lb.stage.style.transform = 'translateY(100vh) scale(.75)';
        lb.shade.style.opacity = '0';
      } else {
        lb.root.classList.remove('is-open');
      }
      if (smooth) setTimeout(finish, 260); else finish();

      const goBack = historyPushed && !fromPop;
      historyPushed = false;
      if (goBack) history.back();
    }

    /* Put every card back the way the rail wants it: muted, looping if it is
       on screen and autoplay is allowed, paused if not. Needed wherever clips
       were paused by something other than scrolling (the viewer opening, the
       tab being hidden), because the observer that plays them only fires
       when visibility CHANGES, and neither of those changes it. */
    function resumeVisible() {
      const rr = rail.getBoundingClientRect();
      cards.forEach((c) => {
        if (!c._video || c === openCard) return;
        const r = c.getBoundingClientRect();
        const seen = Math.min(r.right, rr.right, window.innerWidth) - Math.max(r.left, rr.left, 0);
        const onScreen = seen >= r.width * 0.2 && r.bottom > 0 && r.top < window.innerHeight;
        if (!lite && onScreen) safePlay(c._video); else c._video.pause();
        setState(c);
      });
    }

    window.addEventListener('popstate', () => { if (openCard) closeViewer(false, true); });

    cards.forEach((card) => {
      card.querySelector('[data-op-ugc-btn]').addEventListener('click', () => openViewer(card));
    });

    /* ---- step 3: load, then play only what is on screen ---- */
    const start = () => {
      if (lite || !canMp4) { cards.forEach((c) => buildPoster(c)); return; }
      cards.forEach((c) => buildPoster(c, true));

      // Build a card's video only as it nears the visible part of the rail,
      // so a rail of twelve clips does not open twelve connections at once.
      const loader = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (!en.isIntersecting) return;
          loader.unobserve(en.target);
          buildVideo(en.target);
        });
      }, { rootMargin: '0px 100% 0px 100%' });

      const player = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          const video = en.target._video;
          // The rail can sit on the first screen with only a sliver showing,
          // so "on screen" is any real sliver, and every further step of
          // scroll is another chance to start a clip whose first play() was
          // refused (it used to get exactly one, at the 20% line).
          en.target._inView = en.isIntersecting && en.intersectionRatio >= 0.05;
          if (!video || en.target === openCard) return;
          if (en.target._inView) {
            if (video.paused) safePlay(video);
          } else {
            video.pause();
            video.muted = true;
          }
        });
      }, { threshold: [0, 0.05, 0.2, 0.4, 0.6, 0.8, 1] });

      const playInView = () => {
        // The viewer paused the rail on purpose; closing it resumes it.
        if (openCard) return;
        cards.forEach((c) => {
          if (c._video && c._inView && c !== openCard && c._video.paused) safePlay(c._video);
        });
      };

      Promise.all(cards.map(findSmall)).then(() => {
        cards.forEach((c) => { loader.observe(c); player.observe(c); });
      });

      // A clip that was not ready, or was refused, when its one play() came:
      // try again once it can play, and again on the visitor's first touch or
      // scroll (a gesture lifts the autoplay block of low power mode).
      cards.forEach((c) => {
        const hook = () => { if (c._video) c._video.addEventListener('canplay', playInView, { once: true }); };
        if (c._video) hook(); else c._hookPlay = hook;
      });
      // NOT once, and on touchend and click as well: iOS only counts the END
      // of a touch as a gesture, so a retry on touchstart or scroll is still
      // refused in Low Power Mode, and a single attempt spent on it left the
      // rail frozen for the rest of the visit. The call is nearly free: it
      // only touches clips that are on screen and paused.
      ['touchstart', 'touchend', 'click', 'scroll'].forEach((ev) =>
        window.addEventListener(ev, playInView, { passive: true }));

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) { cards.forEach((c) => c._video && c._video.pause()); return; }
        // Back on the tab: a clip that never left the screen gets no
        // observer callback, so without this it would sit paused.
        if (openCard) { syncViewer(); return; }
        resumeVisible();
      });
    };

    /* ---- frozen-picture watchdog ----
       The failure this guards against is silent: no error event, time keeps
       advancing, audio plays, the picture is stuck. The only tell is that the
       clock moves while the count of presented frames does not. Two such
       seconds in a row and the card gives up on the stream. One timer for the
       whole rail, doing nothing unless a clip is actually playing. */
    if ('getVideoPlaybackQuality' in HTMLVideoElement.prototype) {
      setInterval(() => {
        if (document.hidden) return;
        cards.forEach((c) => {
          const video = c._video;
          if (!video || video.loop || video.paused || video.seeking) { c._wd = null; return; }
          // Clock stopped in the last stretch while "playing": it has run out
          // of media. Same cure as above.
          if (c._wd && video.currentTime === c._wd.t && isFinite(video.duration) && video.currentTime >= video.duration - 1.5) {
            c._wd = null;
            try { video.currentTime = 0; } catch (err) {}
            safePlay(video);
            return;
          }
          if (video.readyState < 3) { c._wd = { t: video.currentTime, frames: -1, strikes: 0 }; return; }
          const frames = video.getVideoPlaybackQuality().totalVideoFrames;
          const t = video.currentTime;
          const w = c._wd;
          if (w && t - w.t > 0.5 && frames === w.frames) {
            w.strikes += 1;
            w.t = t;
            if (w.strikes >= 2) { c._wd = null; c._toMp4(); }
            return;
          }
          c._wd = { t: t, frames: frames, strikes: 0 };
        });
      }, 1000);
    }

    /* ---- steps 1 and 2: after the main photo, and only when nearly in view ---- */
    const arm = () => {
      if (!('IntersectionObserver' in window)) { cards.forEach((c) => buildPoster(c)); return; }
      const gate = new IntersectionObserver((entries) => {
        if (!entries.some((en) => en.isIntersecting)) return;
        gate.disconnect();
        start();
      }, { rootMargin: '100% 0px 100% 0px' });
      gate.observe(root);
    };

    let armed = false;
    const go = () => { if (armed) return; armed = true; arm(); };
    const hero = document.querySelector('.op-pdp__frame.is-active img');
    if (typeof window.opDefer === 'function') {
      // First view first (snippets/op-defer, Sep 2026): the stills and
      // clips wait for the load event, then come in page order, or as soon
      // as the rail nears the screen. Until then the cards are skeletons,
      // which are the cards' exact size, so nothing moves.
      window.opDefer(root, () => {
        if ('IntersectionObserver' in window) start();
        else cards.forEach((c) => buildPoster(c));
      }, { near: 50, wait: 1200 });
    } else if (document.readyState === 'complete' || !hero || hero.complete) go();
    else {
      hero.addEventListener('load', go, { once: true });
      hero.addEventListener('error', go, { once: true });
      setTimeout(go, 2500);
    }
  }
  const setupAll = () => document.querySelectorAll('[data-op-ugc]').forEach(setup);
  setupAll();
  document.addEventListener('shopify:section:load', setupAll);
})();
